import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Link } from "@corpuscomplete/web/components/link";
import { useStore } from "@tanstack/react-form";
import { RecipeForm } from "@corpuscomplete/core-ui/nutrition/recipe/form";
import { NotFoundPage } from "@corpuscomplete/web/components/not-found-page";
import {
  RecipeAuthoringLoadingPage,
  RecipeAuthoringPageShell,
} from "../components/recipe-authoring-page-shell";
import { RecipeFormComponent } from "../components/recipe-form";
import { Scope } from "@corpuscomplete/core/common/scope";
import { ScopeBreadcrumb } from "@corpuscomplete/web/modules/common/components/scope-breadcrumb";
import { FoodCollectionUI } from "@corpuscomplete/core-ui/nutrition/food-collection";
import { useAuth } from "@corpuscomplete/web/auth/hooks";

export function RecipeEditPage({ id }: { id: string }) {
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  if (notFound) {
    return <NotFoundPage />;
  }

  return (
    <RecipeForm.EditProvider
      recipeId={id}
      fallback={<RecipeAuthoringLoadingPage title="Edit Recipe" />}
      onNotFound={() => {
        setSubmitError(null);
        setNotFound(true);
      }}
      onSaved={() => {
        setSubmitError(null);
        void navigate({
          to: "/recipes/$id",
          params: { id },
        });
      }}
      onSaveError={(error) => {
        setSubmitError(error.message);
      }}
    >
      <RecipeEditPageContent
        id={id}
        submitError={submitError}
        onClearError={() => setSubmitError(null)}
      />
    </RecipeForm.EditProvider>
  );
}

function RecipeEditPageContent({
  id,
  submitError,
  onClearError,
}: {
  id: string;
  submitError: string | null;
  onClearError: () => void;
}) {
  const navigate = useNavigate();
  const { userId } = useAuth();
  const form = RecipeForm.useEdit();
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const recipeName = useStore(form.store, (state) => state.values.base.name) || "Recipe";
  const collectionId = useStore(form.store, (state) => state.values.meta.collectionId);
  const collections = FoodCollectionUI.useForContents();
  const collection = collections.find((collection) => collection.id === collectionId);
  const scope = collection ? Scope.fromId({ scopeId: collection.scopeId, userId }) : null;
  const recipesCrumb = scope
    ? {
        label: "Recipes",
        renderLink:
          scope.kind === "personal" ? (
            <Link to="/personal/recipes" />
          ) : (
            <Link to="/spaces/$spaceId/recipes" params={{ spaceId: scope.spaceId }} />
          ),
      }
    : { loading: true as const };

  return (
    <RecipeAuthoringPageShell
      breadcrumb={
        <ScopeBreadcrumb
          scope={scope}
          scopeBehavior="static"
          crumbs={[
            recipesCrumb,
            {
              label: recipeName,
              renderLink: <Link to="/recipes/$id" params={{ id }} />,
            },
            { label: "Edit", current: true },
          ]}
        />
      }
      isSubmitting={isSubmitting}
      submitError={submitError}
      onCancel={() => {
        onClearError();
        void navigate({ to: "/recipes/$id", params: { id } });
      }}
      onSave={() => {
        onClearError();
        void form.handleSubmit();
      }}
    >
      <RecipeFormComponent form={form} />
    </RecipeAuthoringPageShell>
  );
}
